/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Gnoment } from 'shared-ui/gnoment';
import { GuestCounter, Icon, FlexRow, FlexCol, Button } from 'sarsaparilla';
import { getValidationErrorStyle, NoQuotaMods } from '../../utils/utilsNew';
import { LOCAL_PASS, DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS } from '../../constants';

export class GroupMemberCounter extends React.Component {
    static propTypes = {
        guestTypes: PropTypes.arrayOf(PropTypes.shape({})),
        groupMembers: PropTypes.arrayOf(PropTypes.shape({})),
        onChange: PropTypes.func.isRequired,
        minMemberQuota: PropTypes.number,
        maxMemberQuota: PropTypes.number,
        maxPetQuota: PropTypes.number,
        entryDate: PropTypes.instanceOf(Gnoment),
        memberLengthDisabled: PropTypes.bool,
        isQuotaModifyAllowed: PropTypes.bool,
        issuance: PropTypes.shape({}),
        validation: PropTypes.shape({}),
        onModifyDetails: PropTypes.func,
        modifyDetailsButtonText: PropTypes.string,
    };

    static defaultProps = {
        onChange: () => {},
        guestTypes: [
            {
                key: 'adult',
                type: 'Adult',
                description: 'ages 13+',
            },
            {
                key: 'child',
                type: 'Child',
                description: 'ages 0 - 12',
            },
            {
                key: 'local',
                type: 'Adult with Local Pass',
                description: 'no age required',
            },
        ],
        minMemberQuota: 1,
        maxMemberQuota: 1,
        memberLengthDisabled: false,
    };

    constructor(props) {
        super(props);
        this.onInputChange = this.onInputChange.bind(this);
        this.onChangeDetailsClick = this.onChangeDetailsClick.bind(this);
    }

    onInputChange(counts) {
        const newGroupMembers = [];

        if (!this.props.memberLengthDisabled) {
            // TODO update group member list with total counts - will this case ever be used?
        } else {
            // check if dogs breaks the max pet rule
            if (this.props.maxPetQuota && counts && counts.dog > this.props.maxPetQuota) {
                // don't update, too many pets!
                return;
            }
            Object.keys(counts).forEach((key) => {
                for (let i = 0; i < counts[key]; i++) {
                    const newMember = { startDate: this.props.entryDate };
                    if (key === 'local') {
                        newMember.passholderType = LOCAL_PASS;
                        newMember.isAdult = true;
                    } else if (key === 'adult') {
                        newMember.isAdult = true;
                    } else if (key === 'child') {
                        newMember.isChild = true;
                    } else if (key === 'dog') {
                        newMember.isDog = true;
                    }
                    newGroupMembers.push(newMember);
                }
            });
        }

        const groupMemberMax = this.props.issuance.group_size;
        const newMemberSize = newGroupMembers.length;

        if (newMemberSize > groupMemberMax) {
            // too many cooks!
            return;
        }

        this.props.onChange(newGroupMembers);
    }

    onChangeDetailsClick() {
        this.props.onModifyDetails(this.props.issuance);
    }

    getLabel() {
        if (this.props.guestTypes.findIndex((type) => type.key === 'dog') !== -1) {
            return 'Adults/Children/Pets';
        }
        return 'Adults/Children';
    }

    getGroupText() {
        const issuanceGroupSize = this.props.issuance.group_size;

        if (this.props.maxPetQuota) {
            return (
                <p>
                    The quota for this permit is based on the number of people going on
                    the trip. It can only be increased if there are enough spots available
                    for the day. Selected
                    <strong>group size is {issuanceGroupSize} members</strong> at this
                    facility. There are a{' '}
                    <strong>maximum of {this.props.maxPetQuota} pets</strong> allowed at
                    this facilty.
                </p>
            );
        }
        if (this.props.memberLengthDisabled) {
            return (
                <p>
                    The quota for this permit is based on the number of people going on
                    the trip. It can only be increased if there are enough spots available
                    for the day. Selected{' '}
                    <strong>group size is {issuanceGroupSize} members</strong> at this
                    facility.
                </p>
            );
        }
        return null;
    }

    renderChangeDetails() {
        if (this.props.isQuotaModifyAllowed) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        appearance="link"
                        iconBeforeElement={<Icon iconName="edit" />}
                        onClick={this.onChangeDetailsClick}
                        className="change-details-link"
                    >
                        {this.props.modifyDetailsButtonText}
                    </Button>
                </div>
            );
        }
        return NoQuotaMods;
    }

    render() {
        const memberBreakdown = this.props.guestTypes.reduce((acc, type) => {
            acc[type.key] = 0;
            return acc;
        }, {});

        this.props.groupMembers.forEach((member) => {
            if (member.isAdult && member.passholderType === LOCAL_PASS) {
                // make this reference a type  - assumes its been instantiated
                memberBreakdown.local += 1;
            } else if (member.isAdult) {
                memberBreakdown.adult += 1;
            } else if (member.isChild) {
                memberBreakdown.child += 1;
            } else if (member.isDog) {
                memberBreakdown.dog += 1;
            }
        });

        const maxGroupSize = this.props.issuance.group_size;
        const validationStyle = getValidationErrorStyle(
            this.props.validation,
            DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS,
            'rec-error'
        );
        const groupSizeMatchCheck =
            this.props.validation &&
            this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]
                ? this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]
                : null;
        const validationMessage =
            validationStyle &&
            groupSizeMatchCheck &&
            this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]?.message
                ? this.props.validation[DOES_GROUP_SIZE_MATCH_GROUP_MEMBERS]?.message
                : groupSizeMatchCheck;
        return (
            <div
                data-testid="group-member-counter"
                className="per-group-member-form per-section-group"
            >
                <div className="rec-order-detail-subsection-title">Group Size</div>
                {this.getGroupText()}
                <FlexRow>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <label htmlFor="number-of-people">{this.getLabel()}</label>
                            <GuestCounter
                                validationErrorClass={validationStyle}
                                onCountChange={this.onInputChange}
                                value={memberBreakdown}
                                title="Members"
                                maxTotalGuests={maxGroupSize}
                                maxTotalTitle="members"
                                guestTypes={this.props.guestTypes}
                            />
                            {validationStyle && validationMessage}
                        </div>
                    </FlexCol>
                </FlexRow>
                {this.renderChangeDetails()}
            </div>
        );
    }
}

export default GroupMemberCounter;
